import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, salesOrdersAPI } from '../services/api';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import LiveIndicator from '../components/LiveIndicator';
import ActiveUsersWidget from '../components/Admin/ActiveUsersWidget';
import './Dashboard.css';

interface DashboardData {
  metrics: {
    orders_this_week: number;
    orders_change: number;
    pending_production: number;
    revenue_this_month: number;
    revenue_change_percent: number;
    active_customers: number;
  };
  alerts: {
    low_stock: Array<{
      id: number;
      item: string;
      quantity: number;
      unit: string;
      reorder_level: number;
    }>;
    low_stock_count: number;
    expiring_certificates: Array<{
      id: number;
      customer: string;
      customer_id: number;
      days_until_expiration: number;
    }>;
    expiring_certificates_count: number;
  };
  pending_quotes: Array<{
    id: number;
    quote_number: string;
    customer: string;
    customer_id: number;
    total: number;
    status: string;
  }>;
  pending_quotes_count: number;
  ready_to_ship: Array<{
    id: number;
    order_number: string;
    customer: string;
    customer_id: number;
    shipping_method: string;
    delivery_date: string | null;
  }>;
  ready_to_ship_count: number;
  recent_orders: Array<{
    id: number;
    order_number: string;
    customer: string;
    customer_id: number;
    total: number;
    status: string;
    status_display: string;
    date: string;
  }>;
  weekly_data: Array<{
    week: string;
    orders: number;
    revenue: number;
  }>;
}

interface TabCounts {
  needs_attention: number;
  in_progress: number;
  shipped: number;
  drafts: number;
  total: number;
  pending: number;
  ready_to_ship: number;
}

interface ThisWeekOrder {
  id: number;
  order_number: string;
  customer_name: string;
  delivery_date: string;
  status: string;
  total_amount: number;
}

interface ThisWeekData {
  count: number;
  week_start: string;
  week_end: string;
  orders: ThisWeekOrder[];
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [tabCounts, setTabCounts] = useState<TabCounts | null>(null);
  const [thisWeekData, setThisWeekData] = useState<ThisWeekData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Expanded card state
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      // Don't show loading spinner on refresh, only on initial load
      if (!data) setLoading(true);

      // Fetch all data in parallel
      const [dashboardResponse, tabCountsResponse, thisWeekResponse] = await Promise.all([
        dashboardAPI.getSummary(),
        salesOrdersAPI.getTabCounts().catch(() => ({ data: null })),
        salesOrdersAPI.getThisWeek().catch(() => ({ data: null })),
      ]);

      setData(dashboardResponse.data);
      if (tabCountsResponse.data) setTabCounts(tabCountsResponse.data);
      if (thisWeekResponse.data) setThisWeekData(thisWeekResponse.data);
      setError('');
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      if (!data) setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [data]);

  // Initial fetch
  useEffect(() => {
    fetchDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 30 seconds (smart - respects user activity)
  const { isRefreshing, secondsSinceUpdate, isPaused, pauseReason } = useAutoRefresh(fetchDashboardData, {
    intervalMs: 30000,
    enabled: !loading && !error,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'PENDING':
      case 'DRAFT':
        return 'status-pending';
      case 'CONFIRMED':
      case 'SENT':
        return 'status-confirmed';
      case 'PRODUCTION':
        return 'status-production';
      case 'READY':
        return 'status-ready';
      case 'SHIPPED':
        return 'status-shipped';
      case 'DELIVERED':
        return 'status-delivered';
      case 'CANCELLED':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-error">
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="dashboard-page">
      {/* Page Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Dashboard</h1>
          <p className="header-subtitle">Red Bridge Farm Feed Co. Operations Overview</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <LiveIndicator
            isRefreshing={isRefreshing}
            secondsSinceUpdate={secondsSinceUpdate}
            isPaused={isPaused}
            pauseReason={pauseReason}
          />
          <button onClick={fetchDashboardData} className="btn btn-secondary btn-refresh">
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="metrics-row">
        {/* Orders This Week - Expandable */}
        <div
          className={`metric-card expandable ${expandedCard === 'thisWeek' ? 'expanded' : ''}`}
          onClick={() => expandedCard === 'thisWeek' ? setExpandedCard(null) : setExpandedCard('thisWeek')}
        >
          <div className="metric-icon orders-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div className="metric-content">
            <span className="metric-label">Orders This Week</span>
            <span className="metric-value">{thisWeekData?.count ?? data.metrics.orders_this_week}</span>
            <span className={`metric-change ${data.metrics.orders_change >= 0 ? 'positive' : 'negative'}`}>
              {data.metrics.orders_change >= 0 ? '↑' : '↓'} {Math.abs(data.metrics.orders_change)} vs last week
            </span>
          </div>
          <span className="expand-icon">{expandedCard === 'thisWeek' ? '▼' : '▶'}</span>
        </div>
        {expandedCard === 'thisWeek' && thisWeekData && (
          <div className="metric-preview">
            {thisWeekData.orders.length === 0 ? (
              <p className="no-items">No orders scheduled this week</p>
            ) : (
              <ul className="preview-list">
                {thisWeekData.orders.slice(0, 3).map(order => (
                  <li key={order.id} onClick={(e) => { e.stopPropagation(); navigate(`/orders?tab=all`); }}>
                    <span className="preview-name">{order.customer_name}</span>
                    <span className="preview-date">{formatDate(order.delivery_date)}</span>
                  </li>
                ))}
              </ul>
            )}
            <button className="btn-preview-action" onClick={(e) => { e.stopPropagation(); navigate('/orders?tab=all'); }}>
              View All This Week
            </button>
          </div>
        )}

        {/* Needs Confirmation - NEW */}
        <div
          className={`metric-card attention ${(tabCounts?.pending ?? 0) > 0 ? 'has-items' : ''}`}
          onClick={() => navigate('/orders?tab=needs_attention')}
        >
          <div className="metric-icon pending-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="metric-content">
            <span className="metric-label">Needs Confirmation</span>
            <span className="metric-value">{tabCounts?.pending ?? 0}</span>
            <span className="metric-subtext">PENDING orders</span>
          </div>
        </div>

        {/* Pending Production / Confirmed */}
        <div className="metric-card" onClick={() => navigate('/orders?tab=needs_attention')}>
          <div className="metric-icon production-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <div className="metric-content">
            <span className="metric-label">Pending Production</span>
            <span className="metric-value">{tabCounts?.needs_attention ?? data.metrics.pending_production}</span>
            <span className="metric-subtext">CONFIRMED, waiting batch</span>
          </div>
        </div>

        {/* Ready to Ship */}
        <div
          className={`metric-card success ${(tabCounts?.ready_to_ship ?? 0) > 0 ? 'has-items' : ''}`}
          onClick={() => navigate('/orders?tab=in_progress')}
        >
          <div className="metric-icon ready-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="metric-content">
            <span className="metric-label">Ready to Ship</span>
            <span className="metric-value">{tabCounts?.ready_to_ship ?? data.ready_to_ship_count}</span>
            <span className="metric-subtext">READY or PACKAGED</span>
          </div>
        </div>

        {/* Revenue This Month */}
        <div className="metric-card" onClick={() => navigate('/production')}>
          <div className="metric-icon revenue-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="metric-content">
            <span className="metric-label">Revenue This Month</span>
            <span className="metric-value">{formatCurrency(data.metrics.revenue_this_month)}</span>
            <span className={`metric-change ${data.metrics.revenue_change_percent >= 0 ? 'positive' : 'negative'}`}>
              {data.metrics.revenue_change_percent >= 0 ? '↑' : '↓'} {Math.abs(data.metrics.revenue_change_percent)}% MTD
            </span>
          </div>
        </div>
      </div>

      {/* Active Users Widget - Staff only indicator */}
      <div className="active-users-row" style={{ marginBottom: '1.5rem' }}>
        <ActiveUsersWidget />
      </div>

      {/* Action Items Row */}
      <div className="alerts-row">
        {/* Low Stock Alerts */}
        <div className="alert-card warning">
          <div className="alert-header">
            <div className="alert-title">
              <span className="alert-icon">⚠️</span>
              <h3>Low Stock Alerts</h3>
              <span className="alert-count">{data.alerts.low_stock_count}</span>
            </div>
            <button className="btn-link" onClick={() => navigate('/inventory?filter=low_stock')}>
              View All
            </button>
          </div>
          <div className="alert-content">
            {data.alerts.low_stock.length === 0 ? (
              <p className="no-items">No low stock items</p>
            ) : (
              <ul className="alert-list">
                {data.alerts.low_stock.map((item) => (
                  <li key={item.id} onClick={() => navigate('/inventory')}>
                    <span className="item-name">{item.item}</span>
                    <span className="item-detail">
                      {item.quantity} {item.unit} remaining
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Expiring Tax Certificates */}
        <div className="alert-card info">
          <div className="alert-header">
            <div className="alert-title">
              <span className="alert-icon">📋</span>
              <h3>Expiring Tax Certs</h3>
              <span className="alert-count">{data.alerts.expiring_certificates_count}</span>
            </div>
            <button className="btn-link" onClick={() => navigate('/customers?tab=tax')}>
              View All
            </button>
          </div>
          <div className="alert-content">
            {data.alerts.expiring_certificates.length === 0 ? (
              <p className="no-items">No expiring certificates</p>
            ) : (
              <ul className="alert-list">
                {data.alerts.expiring_certificates.map((cert) => (
                  <li key={cert.id} onClick={() => navigate('/customers')}>
                    <span className="item-name">{cert.customer}</span>
                    <span className="item-detail">{cert.days_until_expiration} days</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Pending Quotes */}
        <div className="alert-card neutral">
          <div className="alert-header">
            <div className="alert-title">
              <span className="alert-icon">📝</span>
              <h3>Pending Quotes</h3>
              <span className="alert-count">{data.pending_quotes_count}</span>
            </div>
            <button className="btn-link" onClick={() => navigate('/orders')}>
              View All
            </button>
          </div>
          <div className="alert-content">
            {data.pending_quotes.length === 0 ? (
              <p className="no-items">No pending quotes</p>
            ) : (
              <ul className="alert-list">
                {data.pending_quotes.map((quote) => (
                  <li key={quote.id} onClick={() => navigate('/orders')}>
                    <span className="item-name">{quote.customer}</span>
                    <span className="item-detail">{formatCurrency(quote.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Ready to Ship */}
        <div className="alert-card success">
          <div className="alert-header">
            <div className="alert-title">
              <span className="alert-icon">🚚</span>
              <h3>Ready to Ship</h3>
              <span className="alert-count">{data.ready_to_ship_count}</span>
            </div>
            <button className="btn-link" onClick={() => navigate('/orders?status=READY')}>
              View All
            </button>
          </div>
          <div className="alert-content">
            {data.ready_to_ship.length === 0 ? (
              <p className="no-items">No orders ready to ship</p>
            ) : (
              <ul className="alert-list">
                {data.ready_to_ship.map((order) => (
                  <li key={order.id} onClick={() => navigate('/orders')}>
                    <span className="item-name">#{order.order_number}</span>
                    <span className="item-detail">{order.shipping_method}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders Table */}
      <div className="recent-orders-section">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">📦</span>
            <h2>Recent Orders</h2>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate('/orders')}>
            View All Orders
          </button>
        </div>
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_orders.map((order) => (
                <tr key={order.id} onClick={() => navigate('/orders')}>
                  <td className="order-number">#{order.order_number}</td>
                  <td className="customer-name">{order.customer}</td>
                  <td className="order-total">{formatCurrency(order.total)}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(order.status)}`}>
                      {order.status_display}
                    </span>
                  </td>
                  <td className="order-date">{formatDate(order.date)}</td>
                </tr>
              ))}
              {data.recent_orders.length === 0 && (
                <tr>
                  <td colSpan={5} className="no-orders">No recent orders</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Links */}
      <div className="quick-links-section">
        <h2>Quick Links</h2>
        <div className="quick-links-grid">
          <button className="quick-link-btn primary" onClick={() => navigate('/order-entry')}>
            + New Order
          </button>
          <button className="quick-link-btn primary" onClick={() => navigate('/customers')}>
            + New Customer
          </button>
          <button className="quick-link-btn secondary" onClick={() => navigate('/inventory')}>
            View Inventory
          </button>
          <button className="quick-link-btn secondary" onClick={() => navigate('/production')}>
            Production
          </button>
          <button className="quick-link-btn secondary" onClick={() => navigate('/batches')}>
            Batch Search
          </button>
          <button className="quick-link-btn secondary" onClick={() => navigate('/orders')}>
            All Orders
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
